import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BarChart3Icon, MapPinIcon, CalendarIcon, ZapIcon, ChevronDownIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { motion, AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'How did Viridia help TFP Landscaping get more leads?',
  answer: 'We implemented The Viridia System, which includes GEO-tuned paid ad campaigns and an AI-powered Request a Quote (RAQ) system. By responding instantly to inquiries instead of waiting 4+ hours, TFP was able to capture leads before competitors could.'
}, {
  question: 'What is The Viridia System?',
  answer: 'The Viridia System combines targeted local ad management with our AI lead conversion system. It ensures that the traffic generated by ads is immediately engaged and nurtured into booked appointments.'
}, {
  question: 'How does AI book estimates automatically?',
  answer: "Our AI is integrated directly into the lead platforms your customers use. When a customer requests a quote, the AI instantly replies, asks qualifying questions based on the business's services, and provides a booking link or schedules the estimate directly on the calendar."
}, {
  question: 'What results did TFP Landscaping see in 30 days?',
  answer: 'In just 30 days, TFP Landscaping saw a 284% increase in leads, totaling 242 inbound leads. They achieved a 39.95% close rate on those leads, with 28 estimates booked end-to-end entirely by the AI system.'
}, {
  question: 'How did Viridia help Finish Coat Painting increase revenue?',
  answer: 'Using The Viridia System, we implemented AI-powered messaging to handle inbound lead platform conversations instantly. In 30 days, the AI booked 29 estimates, generating $15.05k in closed-won revenue from end-to-end AI bookings alone - not including revenue from phone calls or website leads.'
}, {
  question: 'Can AI follow-up actually close jobs for contractors?',
  answer: 'Yes. The Finish Coat Painting case study proves that AI follow-up does more than just respond fast - it helps close real jobs. By engaging high-intent local demand immediately, AI turns inquiries into booked estimates and closed revenue.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-2034" data-mp="2mf96vs7dqjxn">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2035" data-mp="2iepbou6d9qe5">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-2036" data-mp="2deazczxqfuab">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-2037" data-mp="23hg5wvgbgk2w" />
      </button>
      <AnimatePresence data-id="element-2038" data-mp="1yckzx7jjovql">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-2039" data-mp="1ybjec94wkqum">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2040" data-mp="1klzdj8uvu3ny">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function CaseStudiesPage() {
  return <div className="bg-viridia-black min-h-screen pt-24" data-id="element-2041" data-mp="rx1zjhxpet10">
      <SEOHead title="Case Studies | Real Results for Local Businesses | Viridia Analytics" description="See how Viridia Analytics helps home service businesses like TFP Landscaping and Finish Coat Painting capture more leads, automate follow-up, and book more jobs." canonical="/case-studies" keywords="marketing case studies, HVAC marketing results, plumber marketing success, local business growth, painters, landscapers, contractors, window installers, marketing agency phoenix" faq={faqs} data-id="element-2042" data-mp="rwimofmwuutu" />

      {/* HERO SECTION */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center" data-id="element-2043" data-mp="3hb0p40h3tutr">
        <ScrollReveal data-id="element-2044" data-mp="3daqbc1n2m04c">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 text-viridia text-sm font-semibold tracking-wide uppercase mb-6 border border-viridia/20" data-id="element-2045" data-mp="3d9f6rzlswoc6">
            PROVEN RESULTS
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tight" data-id="element-2046" data-mp="3mbofcjxie7mx">
            Case Studies: Real Leads, Real Estimates,{' '}
            <span className="text-viridia" data-id="element-2047" data-mp="1rxvxa217da3b">Real Revenue</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" data-id="element-2048" data-mp="7f366fjonhdv">
            These are real client outcomes from AI follow-up, paid ad campaigns,
            and local lead conversion systems. We build systems that actually
            generate revenue for local service businesses.
          </p>
        </ScrollReveal>
      </section>

      {/* CASE STUDIES HUB */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-2049" data-mp="2m0c86azu5x5">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-2050" data-mp="3ks4y8c4npyn7">
          {/* TFP Landscaping Card */}
          <ScrollReveal delay={0.1} data-id="element-2051" data-mp="3pudtjf505csh">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col group hover:border-blue-500/50 transition-colors" data-id="element-2052" data-mp="3avjtzt0gorkf">
              <div className="bg-gray-900 p-8 relative overflow-hidden border-b border-gray-800" data-id="element-2053" data-mp="3fwppfyliasxn">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-[80px]" data-id="element-2054" data-mp="1922q90fgcauy"></div>
                <div className="relative z-10" data-id="element-2055" data-mp="1cm33ji5g3zla">
                  <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2056" data-mp="1cmw2hwy5xksv">
                    TFP Landscaping
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-2057" data-mp="17p2xv8cicyup">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-2058" data-mp="12oyd364khhyo">
                      Landscaping
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-2059" data-mp="1vb9fmmbqhsy4">
                      <MapPinIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-2060" data-mp="1lmrh6bqxfxxn" />
                      Greater Phoenix
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-2061" data-mp="1hk4ne5ap42xt">
                      <ZapIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-2062" data-mp="2e4wv3zhq3ec1" />
                      The Viridia System
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-2063" data-mp="3rzvgw0z6l3g1">
                      <CalendarIcon className="w-3 h-3 mr-1 text-blue-400" data-id="element-2064" data-mp="uwr9ktrrk2x" />
                      Client since July 2025
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2065" data-mp="9ua32k5d8kqt">
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2066" data-mp="estnuh883qhl">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-2067" data-mp="jreq0m71ck8o">
                        284%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2068" data-mp="ojg0c2kn80w2">Lead Increase</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2069" data-mp="six66x3zf01r">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-2070" data-mp="xk4c0qmkdp5s">
                        242
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2071" data-mp="12ifm92gru1oj">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2072" data-mp="dd5zp6m8k6pb">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-2073" data-mp="8fnyy3idgxse">
                        28
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2074" data-mp="ie3qg7lba92f">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2075" data-mp="3quopavk8blrt">
                      <p className="text-2xl font-bold text-blue-400 mb-1" data-id="element-2076" data-mp="3lw3nwst8qw1s">
                        39.9%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2077" data-mp="3ins3qdp7r08">Close Rate</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-grow flex flex-col justify-between" data-id="element-2078" data-mp="374ldrbn2bvqc">
                <p className="text-gray-400 leading-relaxed mb-8" data-id="element-2079" data-mp="33547wh3q4wkn">
                  One-man operation went from 4+ hour response times to instant
                  AI follow-up. Using The Viridia System, TFP
                  captured high-intent local demand and booked estimates
                  automatically.
                </p>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-blue-400 font-bold hover:text-blue-300 transition-colors" data-id="element-2080" data-mp="2j3rirfsiupb8">
                  Read Full Case Study{' '}
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2081" data-mp="3gqi5o7ie1825" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* Finish Coat Painting Card */}
          <ScrollReveal delay={0.2} data-id="element-2082" data-mp="36ritwg2bswax">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl overflow-hidden shadow-2xl h-full flex flex-col group hover:border-orange-500/50 transition-colors" data-id="element-2083" data-mp="36qhho89kq3rm">
              <div className="bg-gray-900 p-8 relative overflow-hidden border-b border-gray-800" data-id="element-2084" data-mp="1vcvtapbjg675">
                <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full blur-[80px]" data-id="element-2085" data-mp="och1yrick1ya"></div>
                <div className="relative z-10" data-id="element-2086" data-mp="taij36w7pr74">
                  <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2087" data-mp="emxh5gb2py0s">
                    Finish Coat Painting
                  </h2>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-2088" data-mp="jl8ptnpmu2hf">
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-2089" data-mp="jjnzkaacp5pd">
                      Painting
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 rounded-md bg-viridia-black border border-gray-700 text-gray-300 text-xs" data-id="element-2090" data-mp="3rth4lcthbken">
                      <ZapIcon className="w-3 h-3 mr-1 text-orange-500" data-id="element-2091" data-mp="nxuge74ft79" />
                      The Viridia System
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-2092" data-mp="1v0xhuthqtrp3">
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2093" data-mp="1uz34c08sx1v1">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-2094" data-mp="3fjhreas73qx0">
                        29
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2095" data-mp="3p8rgdhrqstgw">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2096" data-mp="3kgqaqep2y0zu">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-2097" data-mp="22qsbi8xzjdx">
                        $15.05k
                      </p>
                      <p className="text-gray-400 text-xs font-semibold" data-id="element-2098" data-mp="c2hm1hpohucy">
                        Closed-Won (AI Only)
                      </p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2099" data-mp="747pp3s7wnpr">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-2100" data-mp="2du4hbvglsbf8">
                        138
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2101" data-mp="crmu35kn9dqh">Total Leads</p>
                    </div>
                    <div className="bg-viridia-black/50 p-4 rounded-xl border border-gray-800" data-id="element-2102" data-mp="hqpzws8g7nzi">
                      <p className="text-2xl font-bold text-orange-500 mb-1" data-id="element-2103" data-mp="lsb840a1fe3d">
                        64%
                      </p>
                      <p className="text-gray-400 text-xs" data-id="element-2104" data-mp="qro4bbmstxim">Lead Growth</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-8 flex-grow flex flex-col justify-between" data-id="element-2105" data-mp="voo9lkbtqw8o">
                <p className="text-gray-400 leading-relaxed mb-8" data-id="element-2106" data-mp="10nr8en17o687">
                  AI handled inbound lead platform conversations and booked
                  estimates while the team was on job sites. The Viridia
                  System turned missed opportunities into $15.05k of
                  closed revenue from end-to-end AI bookings alone.
                </p>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-orange-500 font-bold hover:text-orange-400 transition-colors" data-id="element-2107" data-mp="1jh4r9trngm7g">
                  Read Full Case Study{' '}
                  <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2108" data-mp="1jlkkosbyr72u" />
                </Link>
              </div>
            </div>
          </ScrollReveal>

          {/* More Coming Soon Card */}
          <ScrollReveal delay={0.3} data-id="element-2109" data-mp="1fmlhbc5vridi">
            <div className="bg-viridia-surface/50 border border-gray-800 border-dashed rounded-2xl p-8 h-full flex flex-col justify-center items-center text-center relative overflow-hidden min-h-[300px]" data-id="element-2110" data-mp="2carmq1ammesu">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4" data-id="element-2111" data-mp="22jn66x5aylvw">
                <BarChart3Icon className="w-8 h-8 text-gray-500" data-id="element-2112" data-mp="1yibnlbkuj0x7" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2" data-id="element-2113" data-mp="2v19d3z7zkyb1">
                More Success Stories Coming Soon
              </h3>
              <p className="text-sm text-gray-400 max-w-xs" data-id="element-2114" data-mp="2q8zmsi2ylw88">
                We are currently compiling data for our latest client wins.
                Check back soon for more documented results.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2115" data-mp="21to2v87jula2">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2116" data-mp="25toopdwz9yr5">
          <ScrollReveal data-id="element-2117" data-mp="2tjskiz3xcu7l">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2118" data-mp="2tjizlpxqld8r">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-400" data-id="element-2119" data-mp="2kqp72qta4hcy">
              Everything you need to know about working with Viridia Analytics.
            </p>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2120" data-mp="2opoau839y82t">
          <ScrollReveal delay={0.2} data-id="element-2121" data-mp="3cg24g31icezi">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2122" data-mp="3h8box3jusmue">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2123" data-mp="15sb7h8o6kl8p" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden mt-12" data-id="element-2124" data-mp="1ak32pwia7f56">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-2125" data-mp="mtyvlf5vwx9f"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2126" data-mp="hwyr38ooo2kf">
          <ScrollReveal data-id="element-2127" data-mp="wu9b56fe6grr">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2128" data-mp="wqlgg3y7nobg">
              Want to Be Our Next Case Study?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-2129" data-mp="287boztq3ws88">
              Book a free strategy call and let's build a system that captures
              leads, responds faster, and books more jobs for your business.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-2130" data-mp="2abox0yx9zxkn">
              Book Your Free Strategy Call
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2131" data-mp="1rlp7kaxiwggp" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}